import { useToast } from '@chakra-ui/react'
import { DatePickerInput } from '@mantine/dates'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Table from '../../../../../../../components/Table/Table'
import { useGetFinanstilsynetCustomerBreakdownQuery } from '../../../../../../../redux/services/coreApi'
import { setFinanstilsynetBreakdownTableState } from '../../../../../../../redux/slices/tableFiltersSlice'
import type { RootState } from '../../../../../../../redux/store'
import downloadCsv from '../../../../../../../utils/downloadCsv'
import {
  DEFAULT_RANGE,
  type FinanstilsynetDateRange,
  rangeToCsvQuery,
  rangeToFilters,
} from '../dateRange'

const dkkFormatter = new Intl.NumberFormat('da-DK', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

// Country columns in the on-screen table are capped so the UI stays readable;
// the CSV export always contains every country.
const TOP_COUNTRY_COLUMNS = 5

const sumCountryTotals = (
  rows: {
    inboundByCountryDkk: Record<string, number>
    outboundByCountryDkk: Record<string, number>
  }[],
  direction: 'inboundByCountryDkk' | 'outboundByCountryDkk'
): string[] => {
  const totals = new Map<string, number>()
  for (const row of rows) {
    for (const [country, amount] of Object.entries(row[direction])) {
      totals.set(country, (totals.get(country) ?? 0) + (amount ?? 0))
    }
  }
  return Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_COUNTRY_COLUMNS)
    .map(([country]) => country)
}

const CustomerBreakdownTab: React.FC = () => {
  const toast = useToast()
  const [isExporting, setIsExporting] = useState(false)
  const [range, setRange] = useState<FinanstilsynetDateRange>(DEFAULT_RANGE)

  const sessionToken = useSelector((state: RootState) => state.session.token)
  const tableState = useSelector(
    (state: RootState) => state.finanstilsynetBreakdownTable
  )

  const { data: rows, isLoading } = useGetFinanstilsynetCustomerBreakdownQuery(
    rangeToFilters(range)
  )

  const handleExport = async () => {
    if (!sessionToken) {
      toast({
        title: 'Ikke logget ind',
        description: 'Log ind igen for at downloade CSV-filen.',
        status: 'error',
      })
      return
    }
    setIsExporting(true)
    try {
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      await downloadCsv(
        `/compliance/finanstilsynet/customer-breakdown.csv${rangeToCsvQuery(range)}`,
        `penning-kundefordeling-${timestamp}.csv`,
        sessionToken
      )
    } catch (err) {
      toast({
        title: 'Kunne ikke eksportere CSV',
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
      })
    } finally {
      setIsExporting(false)
    }
  }

  const topInboundCountries = useMemo(
    () => sumCountryTotals(rows ?? [], 'inboundByCountryDkk'),
    [rows]
  )
  const topOutboundCountries = useMemo(
    () => sumCountryTotals(rows ?? [], 'outboundByCountryDkk'),
    [rows]
  )

  const tableRows = useMemo(
    () =>
      (rows ?? []).map(r => {
        const inboundCells: Record<string, number> = {}
        const outboundCells: Record<string, number> = {}
        for (const country of topInboundCountries) {
          inboundCells[`in_${country}`] = r.inboundByCountryDkk[country] ?? 0
        }
        for (const country of topOutboundCountries) {
          outboundCells[`out_${country}`] = r.outboundByCountryDkk[country] ?? 0
        }
        return {
          ...r,
          ...inboundCells,
          ...outboundCells,
          id: r.customerNumber,
        }
      }),
    [rows, topInboundCountries, topOutboundCountries]
  )

  const extraInboundColumns = topInboundCountries.map((country, idx) => {
    const accessorKey = `in_${country}`
    return {
      header: `Ind ${country} (DKK)`,
      accessorKey,
      type: 'number' as const,
      priority: 20 + idx,
      cellValueWrapper: (
        _value: string,
        _originalValue: string,
        row: { original: Record<string, unknown> }
      ) => {
        const raw = Number(row.original[accessorKey])
        return <div>{dkkFormatter.format(Number.isFinite(raw) ? raw : 0)}</div>
      },
    }
  })

  const extraOutboundColumns = topOutboundCountries.map((country, idx) => {
    const accessorKey = `out_${country}`
    return {
      header: `Ud ${country} (DKK)`,
      accessorKey,
      type: 'number' as const,
      priority: 30 + idx,
      cellValueWrapper: (
        _value: string,
        _originalValue: string,
        row: { original: Record<string, unknown> }
      ) => {
        const raw = Number(row.original[accessorKey])
        return <div>{dkkFormatter.format(Number.isFinite(raw) ? raw : 0)}</div>
      },
    }
  })

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div className="max-w-3xl text-sm text-brand-light-text-muted">
          Fordeling af alle kunder på privat- og erhvervskunder med
          onboarding-dato, aktivitet og geografi. Felterne risikoklassifikation,
          branche og offboarding-årsag udfyldes manuelt. Tabellen viser de{' '}
          {TOP_COUNTRY_COLUMNS} mest beløbsaktive lande pr. retning – CSV-filen
          indeholder alle lande.
        </div>
        <button
          type="button"
          className="button"
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? 'Eksporterer…' : 'Eksportér CSV'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium">Periode (inkl. slutdato)</label>
        <DatePickerInput
          type="range"
          value={range}
          onChange={setRange}
          valueFormat="D MMM YYYY"
          allowSingleDateInRange
          clearable={false}
          classNames={{
            month: 'w-auto',
            input:
              '!rounded-lg !border-none !shadow-sm hover:bg-brand-gray-100 !px-3 !py-2.5 !text-sm !font-medium !flex !justify-between !items-center',
          }}
        />
        <span className="text-xs text-brand-light-text-muted">
          Begge datoer er inkluderet i udtrækket.
        </span>
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 text-sm">
        <strong>Bemærk:</strong> Tabellen herunder er kun en forhåndsvisning og
        viser ikke nødvendigvis alle kolonner og værdier (bl.a. kun de{' '}
        {TOP_COUNTRY_COLUMNS} mest aktive lande pr. retning, og
        offboarding-dato vises ikke i tabellen). Den fulde og autoritative
        indberetning til Finanstilsynet — afgrænset til den valgte periode —
        findes ved at trykke på "Eksportér CSV".
      </div>

      <Table
        key={`breakdown:${topInboundCountries.join(',')}|${topOutboundCountries.join(',')}`}
        tableState={tableState}
        setTableState={setFinanstilsynetBreakdownTableState}
        navigate={() => null}
        onClickRow={() => null}
        isLoading={isLoading}
        emptyDataRender={<div className="text-xl mb-3">Ingen data</div>}
        defaultData={tableRows}
        schema={[
          {
            header: 'Kundetype',
            accessorKey: 'customerType',
            type: 'status',
            priority: 1,
          },
          {
            header: 'Kundenummer',
            accessorKey: 'customerNumber',
            type: 'string',
            priority: 2,
          },
          {
            cardTitle: true,
            header: 'Navn',
            accessorKey: 'name',
            type: 'string',
            priority: 3,
          },
          {
            header: 'Onboarding',
            accessorKey: 'onboardedAt',
            type: 'date',
            priority: 4,
          },
          {
            header: 'Aktivitet',
            accessorKey: 'offeredActivity',
            type: 'string',
            priority: 5,
          },
          {
            header: 'Geografi',
            accessorKey: 'geographicAffiliation',
            type: 'status',
            priority: 6,
          },
          {
            header: 'Status',
            accessorKey: 'status',
            type: 'status',
            priority: 7,
          },
          ...extraInboundColumns,
          ...extraOutboundColumns,
        ]}
      />
    </div>
  )
}

export default CustomerBreakdownTab
